'use client';

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Modal from '../Modal/Modal';
import Icon from '../Icon/Icon';
import Button from '../Button/Button';
import s from './LatestUpdatesModal.module.scss';

const LatestUpdatesModal = ({
    title = '',
    slides = [],
    buttonContent,
    isOpen = false,
    onClose,
}) => {
    const [currentSlide, setCurrentSlide] = useState(0);

    useEffect(() => {
        if (!isOpen) setCurrentSlide(0);
    }, [isOpen]);

    const handleNext = () => {
        if (currentSlide < slides.length - 1) {
            setCurrentSlide((prev) => prev + 1);
        } else if (onClose) {
            onClose();
        }
    };

    const slide = slides[currentSlide];
    const isLastSlide = currentSlide === slides.length - 1;

    if (!slide) return null;

    return (
        <Modal
            title={title}
            buttonContent={buttonContent}
            isOpen={isOpen}
            onClose={onClose}>
            <div className={s.Media}>
                {slide.mediaType === 'video' ? (
                    <video
                        key={currentSlide}
                        src={slide.mediaSrc}
                        autoPlay
                        loop
                        muted
                        playsInline
                        className={s.MediaEl}
                    />
                ) : (
                    <img
                        key={currentSlide}
                        src={slide.mediaSrc}
                        alt={slide.mediaAlt || ''}
                        className={s.MediaEl}
                    />
                )}
            </div>

            <div className={s.Body}>
                {slides.length > 1 && (
                    <div className={s.Dots}>
                        {slides.map((_, i) => (
                            <button
                                key={i}
                                className={classNames(s.Dot, {
                                    [s.DotActive]: i === currentSlide,
                                })}
                                onClick={() => setCurrentSlide(i)}
                                aria-label={`Slide ${i + 1}`}
                            />
                        ))}
                    </div>
                )}

                <h3 className={s.SlideTitle}>{slide.title}</h3>
                {slide.description && (
                    <p className={s.SlideDescription}>{slide.description}</p>
                )}
                {slide.buttonText && slide.buttonUrl && (
                    <Button
                        className={s.SlideButton}
                        title={slide.buttonText}
                        url={slide.buttonUrl}
                        modifier="Primary"
                        size="Large"
                        icon="Arrow"
                    />
                )}
            </div>

            <div className={s.Footer}>
                {currentSlide > 0 && (
                    <button
                        type="button"
                        className={classNames(s.Button, s['Button--Prev'])}
                        onClick={() => setCurrentSlide((prev) => prev - 1)}>
                        <div className={s.ButtonInner}>
                            <span className={s.ButtonInnerText}>Previous</span>
                            <div className={s.ButtonInnerIcon}>
                                <Icon name="ArrowLeftBlack" />
                            </div>
                        </div>
                    </button>
                )}
                <button
                    type="button"
                    className={classNames(s.Button, s['Button--Next'])}
                    onClick={handleNext}>
                    <div className={s.ButtonInner}>
                        <span className={s.ButtonInnerText}>
                            {isLastSlide ? 'Get started' : 'Next'}
                        </span>
                        <div className={s.ButtonInnerIcon}>
                            <Icon name="ArrowRightBlack" />
                        </div>
                    </div>
                </button>
            </div>
        </Modal>
    );
};

LatestUpdatesModal.propTypes = {
    title: PropTypes.string,
    slides: PropTypes.arrayOf(
        PropTypes.shape({
            mediaType: PropTypes.oneOf(['image', 'video']),
            mediaSrc: PropTypes.string,
            mediaAlt: PropTypes.string,
            title: PropTypes.string,
            description: PropTypes.string,
        })
    ),
    buttonContent: PropTypes.node,
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
};

export default LatestUpdatesModal;
